import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ConfigProvider, App as AntApp } from "antd";
import { themeConfig } from "./theme";
import "antd/dist/reset.css";

import Login from "./components/Login";
import Admin from "./components/Admin";
import ExistingUser from "./components/UserDetails";
import ApiCollection from "./components/ApiCollection";
import AddUser from "./components/AddUser";
import RCDelete from "./components/RCDelete";
import ApiDocumentation from "./components/ApiDocumentation";
import RESTQueryBuilder from "./components/RESTQueryBuilder";
import S3FileManager from "./components/S3FileManager";
import PrivateRoute from "./components/PrivateRoute";
import RoleBasedRoute from "./components/RoleBasedRoute";
import ConsoleLogin from "./components/ConsoleLogin";
import ConsoleInvite from "./components/ConsoleInvite";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import RequireAuth from "./console/RequireAuth";
import RequireRole from "./console/RequireRole";
import ConsoleLayout from "./components/console/ConsoleLayout";
import ConsoleHome from "./components/console/ConsoleHome";
import ConsoleDocs from "./components/console/ConsoleDocs";
import ConsoleMyApis from "./components/console/ConsoleMyApis";
import ConsoleUsers from "./components/console/ConsoleUsers";
import ConsoleInvites from "./components/console/ConsoleInvites";
import ConsoleAccount from "./components/console/ConsoleAccount";
import ConsoleAuditLog from "./components/console/ConsoleAuditLog";
import ConsoleAccounts from "./components/console/ConsoleAccounts";
import ConsoleAccountDetail from "./components/console/ConsoleAccountDetail";
import ConsoleBrands from "./components/console/ConsoleBrands";
import { ADMIN_ROLES, ROLES } from "./console/consoleUtils";

const App = () => {
  // bfcache guard: when a page is restored from the back-forward cache
  // (event.persisted), React never re-runs, so route guards can't re-check auth —
  // a logged-out user pressing Back would see a stale authenticated page. If the
  // restored page is one we cached while logged out, force a reload so guards run.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted && !localStorage.getItem("isAuthenticated")) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return (
    <ConfigProvider theme={themeConfig}>
      <AntApp>
        <Router>
          <div className="min-h-screen flex flex-col">
            <Routes>
              {/* Home is the Console. Sending it to /console (not /console/login) lets
                  RequireAuth decide: signed-out users land on the login page, signed-in
                  users go straight into the Console instead of seeing a login form.
                  The legacy Homepage/dashboard are no longer the entry point. */}
              <Route path="/" element={<Navigate to="/console" replace />} />
              <Route path="/login" element={<Login />} />
              {/* VER-218: VerifyU Console session login (coexists with legacy /login) */}
              <Route path="/console/login" element={<ConsoleLogin />} />
              {/* VER-218: invitee set-password page (target of the invite email link) */}
              <Route path="/console/invite/:token" element={<ConsoleInvite />} />
              {/* VER-218: password reset */}
              <Route path="/console/forgot-password" element={<ForgotPassword />} />
              <Route path="/console/reset-password/:token" element={<ResetPassword />} />
              {/* Productization: role-aware Console. Open to any authenticated user
                  (client dashboard); member-management + internal sections gated per-route. */}
              <Route
                path="/console"
                element={
                  <RequireAuth>
                    <ConsoleLayout />
                  </RequireAuth>
                }
              >
                <Route index element={<ConsoleHome />} />
                {/* Client dashboard — every authenticated user (account_admin + member) */}
                <Route path="docs" element={<ConsoleDocs />} />
                <Route path="my-apis" element={<ConsoleMyApis />} />
                {/* Member management — account_admin (own account) + internal_admin (all) */}
                <Route
                  path="users"
                  element={
                    <RequireRole roles={ADMIN_ROLES}>
                      <ConsoleUsers />
                    </RequireRole>
                  }
                />
                <Route
                  path="invites"
                  element={
                    <RequireRole roles={ADMIN_ROLES}>
                      <ConsoleInvites />
                    </RequireRole>
                  }
                />
                <Route
                  path="account"
                  element={
                    <RequireRole roles={ADMIN_ROLES}>
                      <ConsoleAccount />
                    </RequireRole>
                  }
                />
                <Route
                  path="audit-log"
                  element={
                    <RequireRole roles={ADMIN_ROLES}>
                      <ConsoleAuditLog />
                    </RequireRole>
                  }
                />
                {/* Internal Admin only — create/manage client tenants */}
                <Route
                  path="accounts"
                  element={
                    <RequireRole roles={[ROLES.INTERNAL_ADMIN]}>
                      <ConsoleAccounts />
                    </RequireRole>
                  }
                />
                {/* Drill-in: manage one company's people (Members + Invites scoped to it) */}
                <Route
                  path="accounts/:accountId"
                  element={
                    <RequireRole roles={[ROLES.INTERNAL_ADMIN]}>
                      <ConsoleAccountDetail />
                    </RequireRole>
                  }
                />
                <Route
                  path="brands"
                  element={
                    <RequireRole roles={[ROLES.INTERNAL_ADMIN]}>
                      <ConsoleBrands />
                    </RequireRole>
                  }
                />
                {/* Internal tools (internal_admin only) — the reused legacy ops screens.
                    They wrap themselves in DashboardLayout, which renders children-only
                    inside the Console (see console/insideConsole.js), so no double shell. */}
                <Route
                  path="tools/api-collection"
                  element={
                    <RequireRole roles={[ROLES.INTERNAL_ADMIN]}>
                      <ApiCollection />
                    </RequireRole>
                  }
                />
                <Route
                  path="tools/query-builder"
                  element={
                    <RequireRole roles={[ROLES.INTERNAL_ADMIN]}>
                      <RESTQueryBuilder />
                    </RequireRole>
                  }
                />
                <Route
                  path="tools/s3-files"
                  element={
                    <RequireRole roles={[ROLES.INTERNAL_ADMIN]}>
                      <S3FileManager />
                    </RequireRole>
                  }
                />
                <Route
                  path="tools/rc-delete"
                  element={
                    <RequireRole roles={[ROLES.INTERNAL_ADMIN]}>
                      <RCDelete />
                    </RequireRole>
                  }
                />
              </Route>
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Admin />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <Admin />
                  </PrivateRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <RoleBasedRoute allowedRoles={["admin"]}>
                    <ExistingUser />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/api-collection"
                element={
                  <RoleBasedRoute allowedRoles={["admin"]}>
                    <ApiCollection />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/add-user"
                element={
                  <RoleBasedRoute allowedRoles={["admin"]}>
                    <AddUser />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/rc/delete"
                element={
                  <RoleBasedRoute allowedRoles={["admin"]}>
                    <RCDelete />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/documentation"
                element={
                  <PrivateRoute>
                    <ApiDocumentation />
                  </PrivateRoute>
                }
              />
              <Route
                path="/rest-query-builder"
                element={
                  <PrivateRoute>
                    <RESTQueryBuilder />
                  </PrivateRoute>
                }
              />
              <Route
                path="/s3-files"
                element={
                  <PrivateRoute>
                    <S3FileManager />
                  </PrivateRoute>
                }
              />
            </Routes>
          </div>
        </Router>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
